package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTransformGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import kotlin.math.PI
import kotlin.math.atan
import kotlin.math.cos
import kotlin.math.floor
import kotlin.math.ln
import kotlin.math.pow
import kotlin.math.sinh
import kotlin.math.tan

/**
 * A tappable map marker. [lat]/[lng] are WGS84; [selected] draws it larger in the accent color.
 * With [label] set the marker renders as a circular avatar chip (initials + per-id color) instead of
 * the classic pin — used for people; businesses keep the pin. [avatarUrl] (when present) fills the
 * chip with the person's photo.
 */
data class MapPin(
    val id: String,
    val lat: Double,
    val lng: Double,
    val selected: Boolean = false,
    val label: String? = null,
    val avatarUrl: String? = null,
)

/**
 * Pure-Compose "slippy" tile map (Web-Mercator raster tiles + pins + optional radius overlay).
 *
 * WHY hand-rolled: the web target renders inside a skiko canvas, so DOM map SDKs (Google JS,
 * Leaflet) cannot be embedded, and Google Maps Compose is Android-only. Drawing standard XYZ
 * raster tiles with Coil is the one approach that ships identically on Android, iOS and web from
 * `commonMain`. Pan and pinch-zoom are handled with transform gestures; zoom is continuous, with
 * tiles fetched at the nearest integer level and scaled between levels.
 *
 * [center]/[zoom] are hoisted (controlled) state so screens can recenter or preset zoom.
 * [attribution] is REQUIRED map-data credit (e.g. "© OpenStreetMap") — tile policy, not decoration.
 * [tileUrlTemplate] defaults to the public OSM server, acceptable for development volumes only;
 * swap in a keyed provider (MapTiler/Carto) via this parameter before production scale.
 */
@Composable
fun TileMap(
    center: MapPoint,
    zoom: Double,
    onCameraChange: (center: MapPoint, zoom: Double) -> Unit,
    pins: List<MapPin>,
    onPinClick: (String) -> Unit,
    attribution: String,
    modifier: Modifier = Modifier,
    pinContentDescription: String? = null,
    /**
     * Localized a11y label for a clustered marker, given its member count. Null falls back to
     * [pinContentDescription]. A lambda rather than a String because the count varies per marker,
     * and design-system components carry no copy of their own.
     */
    clusterContentDescription: ((Int) -> String)? = null,
    userLocation: MapPoint? = null,
    radiusKm: Double? = null,
    radiusCenter: MapPoint? = null,
    tileUrlTemplate: String = OSM_TILE_URL,
    /** Localized labels for the built-in zoom controls; the buttons hide when either is null. */
    zoomInContentDescription: String? = null,
    zoomOutContentDescription: String? = null,
    /** See `AppMap`: a tap on pins that zooming cannot separate, answered by the screen as a list. */
    onClusterPinsClick: (List<String>) -> Unit = {},
) {
    BoxWithConstraints(
        modifier = modifier
            .background(AppTheme.colors.surfaceContainer)
            .pointerInput(Unit) {
                detectTransformGestures { _, pan, gestureZoom, _ ->
                    val density = this.density
                    val newZoom = (zoom + ln(gestureZoom.toDouble()) / LN_2).coerceIn(MIN_ZOOM, MAX_ZOOM)
                    // Pan is in px; world coordinates are in dp. Keep the point under the finger
                    // fixed for pan; zoom is about the view center (simple + stable).
                    val world = worldSize(newZoom)
                    val cx = lngToWorldX(center.lng, world) - pan.x / density
                    val cy = latToWorldY(center.lat, world) - pan.y / density
                    onCameraChange(MapPoint(worldYToLat(cy, world), worldXToLng(cx, world)), newZoom)
                }
            }
            // Mouse users can't pinch: scroll wheel zooms about the view center (web/desktop).
            .pointerInput(Unit) {
                awaitPointerEventScope {
                    while (true) {
                        val event = awaitPointerEvent()
                        if (event.type == PointerEventType.Scroll) {
                            val dy = event.changes.firstOrNull()?.scrollDelta?.y ?: 0f
                            if (dy != 0f) {
                                val newZoom = (zoom - dy * SCROLL_ZOOM_STEP).coerceIn(MIN_ZOOM, MAX_ZOOM)
                                onCameraChange(center, newZoom)
                                event.changes.forEach { it.consume() }
                            }
                        }
                    }
                }
            },
    ) {
        val density = LocalDensity.current
        val widthDp = maxWidth.value.toDouble()
        val heightDp = maxHeight.value.toDouble()
        val world = worldSize(zoom)
        val centerX = lngToWorldX(center.lng, world)
        val centerY = latToWorldY(center.lat, world)
        val originX = centerX - widthDp / 2 // world dp coordinate of the view's top-left
        val originY = centerY - heightDp / 2

        // Tiles are fetched at the nearest integer zoom and drawn scaled to the continuous zoom.
        val tileZ = zoom.toInt().coerceIn(MIN_ZOOM.toInt(), MAX_TILE_ZOOM)
        val tileCount = 1 shl tileZ
        val tileSpan = world / tileCount // on-screen dp size of one tile
        val firstTileX = floor(originX / tileSpan).toInt()
        val lastTileX = floor((originX + widthDp) / tileSpan).toInt()
        val firstTileY = floor(originY / tileSpan).toInt().coerceAtLeast(0)
        val lastTileY = floor((originY + heightDp) / tileSpan).toInt().coerceAtMost(tileCount - 1)

        for (tx in firstTileX..lastTileX) {
            // Wrap x so panning across the antimeridian never shows a void.
            val wrappedX = ((tx % tileCount) + tileCount) % tileCount
            for (ty in firstTileY..lastTileY) {
                key(tileZ, tx, ty) {
                    AsyncImage(
                        model = tileUrlTemplate
                            .replace("{z}", tileZ.toString())
                            .replace("{x}", wrappedX.toString())
                            .replace("{y}", ty.toString()),
                        contentDescription = null,
                        contentScale = ContentScale.FillBounds,
                        modifier = Modifier
                            .offset(
                                x = (tx * tileSpan - originX).dp,
                                y = (ty * tileSpan - originY).dp,
                            )
                            // +0.5dp bleed hides hairline seams from fractional scaling.
                            .size((tileSpan + 0.5).dp),
                    )
                }
            }
        }

        // Search-radius ring + user-location dot, drawn over tiles but under pins.
        Canvas(modifier = Modifier.fillMaxSize()) {
            if (radiusKm != null && radiusCenter != null) {
                val cxDp = lngToWorldX(radiusCenter.lng, world) - originX
                val cyDp = latToWorldY(radiusCenter.lat, world) - originY
                // Project a point radiusKm east of the center to get the on-screen radius.
                val lngPerKm = 1.0 / (111.320 * cos(radiusCenter.lat * PI / 180.0))
                val edgeX = lngToWorldX(radiusCenter.lng + radiusKm * lngPerKm, world) - originX
                val radiusPx = ((edgeX - cxDp) * density.density).toFloat()
                val c = Offset((cxDp * density.density).toFloat(), (cyDp * density.density).toFloat())
                drawCircle(color = radiusFill, radius = radiusPx, center = c)
                drawCircle(color = radiusStroke, radius = radiusPx, center = c, style = Stroke(width = 2f * density.density))
            }
            if (userLocation != null) {
                val ux = (lngToWorldX(userLocation.lng, world) - originX) * density.density
                val uy = (latToWorldY(userLocation.lat, world) - originY) * density.density
                val c = Offset(ux.toFloat(), uy.toFloat())
                drawCircle(color = Color.White, radius = 8f * density.density, center = c)
                drawCircle(color = userDot, radius = 5.5f * density.density, center = c)
            }
        }

        // Pins — anchor is the marker's tip (bottom-center of the icon). Selected pin draws last
        // (on top) and larger.
        //
        // Project first, cull, then CLUSTER: with real prod coordinates ~150 GTA businesses at
        // metro zoom overlap into one opaque blob. Clustering is in screen space, so bubbles split
        // on their own as zoom rises. A cluster of one renders down the original path unchanged —
        // that is what keeps this wiring low-risk.
        val clusterCellDp = AppTheme.sizes.avatarMd.value.toDouble()
        val screenPins = pins.mapNotNull { pin ->
            val px = lngToWorldX(pin.lng, world) - originX
            val py = latToWorldY(pin.lat, world) - originY
            if (px < -PIN_CULL_DP || px > widthDp + PIN_CULL_DP ||
                py < -PIN_CULL_DP || py > heightDp + PIN_CULL_DP
            ) {
                null
            } else {
                ScreenPin(pin, px, py)
            }
        }
        clusterPins(screenPins, clusterCellDp)
            .sortedBy { it.single?.selected == true }
            .forEach { cluster ->
                val pin = cluster.single
                if (pin == null) {
                    // Counted bubble. Tapping it flies to the members' mean position and zooms in,
                    // which is what splits the bubble apart on the next frame.
                    val bubble = AppTheme.sizes.avatarMd
                    Box(
                        modifier = Modifier
                            .offset(
                                x = (cluster.x - bubble.value / 2).dp,
                                y = (cluster.y - bubble.value / 2).dp,
                            )
                            .clickable {
                                // Pins on one spot (or a map already at max zoom) cannot be split
                                // by flying closer — surface them as a list instead.
                                if (cluster.members.shareOneSpot() || atMaxZoom(zoom)) {
                                    onClusterPinsClick(cluster.members.map { it.id })
                                } else {
                                    val meanLat = cluster.members.sumOf { it.lat } / cluster.count
                                    val meanLng = cluster.members.sumOf { it.lng } / cluster.count
                                    onCameraChange(
                                        MapPoint(meanLat, meanLng),
                                        (zoom + MAP_CLUSTER_ZOOM_STEP).coerceAtMost(MAX_ZOOM),
                                    )
                                }
                            }
                            .semantics {
                                val label = clusterContentDescription?.invoke(cluster.count)
                                    ?: pinContentDescription
                                if (label != null) contentDescription = label
                            },
                    ) {
                        ClusterBubbleVisual(cluster.count)
                    }
                    return@forEach
                }
                val px = cluster.x
                val py = cluster.y
            // Person chips anchor at their centre, business pins at the glyph's bottom-centre.
            val chip = if (pin.selected) AppTheme.sizes.avatarLg else AppTheme.sizes.avatarMd
            val glyph = if (pin.selected) AppTheme.sizes.iconHero else AppTheme.sizes.iconLg
            val (dx, dy) = if (pin.label != null) chip.value / 2 to chip.value / 2 else glyph.value / 2 to glyph.value
            Box(
                modifier = Modifier
                    .offset(x = (px - dx).dp, y = (py - dy).dp)
                    .clickable { onPinClick(pin.id) },
            ) {
                PinMarkerVisual(pin = pin, contentDescription = pinContentDescription)
            }
            }

        // Built-in zoom controls — mouse/touch-agnostic (pinch has no desktop equivalent).
        if (zoomInContentDescription != null && zoomOutContentDescription != null) {
            androidx.compose.foundation.layout.Column(
                modifier = Modifier
                    .align(Alignment.CenterEnd)
                    .padding(AppTheme.spacing.md),
            ) {
                Box(modifier = Modifier.background(Color.White, AppTheme.shapes.pill)) {
                    AppIconButton(
                        iconName = "add",
                        contentDescription = zoomInContentDescription,
                        onClick = { onCameraChange(center, (zoom + BUTTON_ZOOM_STEP).coerceIn(MIN_ZOOM, MAX_ZOOM)) },
                    )
                }
                androidx.compose.foundation.layout.Spacer(Modifier.size(AppTheme.spacing.sm))
                Box(modifier = Modifier.background(Color.White, AppTheme.shapes.pill)) {
                    AppIconButton(
                        iconName = "remove",
                        contentDescription = zoomOutContentDescription,
                        onClick = { onCameraChange(center, (zoom - BUTTON_ZOOM_STEP).coerceIn(MIN_ZOOM, MAX_ZOOM)) },
                    )
                }
            }
        }

        // Required tile-data credit. Text color is FIXED dark (not a theme token): the scrim below is a
        // fixed light value (tiles are always light), so a theme-variable foreground would go
        // light-on-light and vanish the legally-required credit in dark theme.
        Text(
            text = attribution,
            style = AppTheme.typography.labelSm,
            color = attributionText,
            modifier = Modifier
                .align(Alignment.BottomStart)
                .background(scrimBackground)
                .padding(horizontal = AppTheme.spacing.xs),
        )
    }
}

/** A WGS84 point for the map camera (kept DS-local so the design system stays model-free). */
data class MapPoint(val lat: Double, val lng: Double)

private const val OSM_TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png"
private const val MIN_ZOOM = MAP_MIN_ZOOM
private const val MAX_ZOOM = MAP_MAX_ZOOM
private const val MAX_TILE_ZOOM = 18
private const val PIN_CULL_DP = 64.0
private const val SCROLL_ZOOM_STEP = 0.5
private const val BUTTON_ZOOM_STEP = 1.0

// Overlay colors: fixed light-on-map values — tile imagery is always light, so these do not
// follow the app theme (a dark-theme scrim over a light map would be wrong in both themes).
private val radiusFill = radiusFillColor
private val radiusStroke = radiusStrokeColor
private val userDot = userLocationDot
private val scrimBackground = Color(0xB3FFFFFF)
// Fixed near-black credit text, paired with the fixed light scrim so it stays legible in both themes.
private val attributionText = Color(0xDD000000)

private val LN_2 = ln(2.0)

/** World size in dp at a continuous [zoom] (256dp tiles at z0, doubling per level). */
private fun worldSize(zoom: Double): Double = 256.0 * 2.0.pow(zoom)

private fun lngToWorldX(lng: Double, world: Double): Double = (lng + 180.0) / 360.0 * world

private fun latToWorldY(lat: Double, world: Double): Double {
    val rad = lat * PI / 180.0
    return (1.0 - ln(tan(rad) + 1.0 / cos(rad)) / PI) / 2.0 * world
}

private fun worldXToLng(x: Double, world: Double): Double = x / world * 360.0 - 180.0

private fun worldYToLat(y: Double, world: Double): Double =
    atan(sinh(PI * (1.0 - 2.0 * y / world))) * 180.0 / PI
